/* MS GIFT LADDER ENGINE
   Subscription only free gifts, tiered by supply length. UX layer ONLY: the money layer
   is the "Subscriber Welcome Gifts" discount Function, which zeroes gift lines server
   side and only when a ceremonial subscription line is present. This file can fail
   completely and nothing ever becomes free by mistake.

   Tiers (months of supply -> gifts):
     1+ month  -> Bamboo Scoop + Matcha Sifter
     2+ months -> + Bamboo Whisk
     4+ months -> + Hojicha Powder 50g

   THE INVARIANT (fail closed): a gift line may exist only while it is actually
   rendering at $0. If the discount is not applying (code conflict, discount disabled,
   anything), the engine REMOVES the gift line and remembers not to re add it for the
   life of this cart. Worst case a customer misses a gift; free goods can never leak.

   Inherited from the July engine (proven on this theme family) and IM8's live engine:
   - ONE reconciler, mutex + queue, never two mutations racing
   - verify after write: re read /cart.js after every mutation pass
   - state in localStorage keyed to the cart token (resets when Shopify issues a new cart)
   - shopper removals respected for the life of the cart
   - gift lines targeted by line key, never index
   - quantity clamped back to 1 (the Function also only zeroes quantity 1)
   - bfcache pageshow revalidation, fetch patch so ANY cart mutation triggers reconcile
*/
(function () {
  'use strict';
  if (window.__msGiftLadder) return;
  window.__msGiftLadder = true;

  var BUSY = false;
  var QUEUED = false;
  var STORE_KEY = 'msGiftLadderState';
  var DEBOUNCE = null;

  /* ------------------------------------------------------------------ config */

  var CFG = null;
  function config() {
    if (CFG) return CFG;
    var el = document.getElementById('ms-gift-config');
    if (!el) return null; /* B2B or not rendered: engine inert */
    try { CFG = JSON.parse(el.textContent); } catch (e) { return null; }
    if (!CFG || !CFG.gifts || !CFG.products) { CFG = null; return null; }
    CFG.giftByVariant = {};
    CFG.gifts.forEach(function (g) { CFG.giftByVariant[g.variant] = g; });
    return CFG;
  }

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  /* ------------------------------------------------------- tier derivation */

  function monthsForLine(item, cfg) {
    var alloc = item.selling_plan_allocation;
    if (!alloc || !alloc.selling_plan) return 0;           /* one time: no gifts, ever */
    if (!cfg.products[String(item.product_id)]) return 0;  /* out of scope product */
    var planId = String(alloc.selling_plan.id);
    if (cfg.plans[planId]) return cfg.plans[planId];       /* supply plans: the truth */
    /* Legacy monthly plans and any future plan: months from the variant pack size,
       falling back to a "N-Month" parse of the plan name. */
    var product = cfg.products[String(item.product_id)];
    var byVariant = product.variants[String(item.variant_id)];
    if (byVariant) return byVariant;
    var m = /(\d+)\s*-?\s*month/i.exec(alloc.selling_plan.name || '');
    return m ? parseInt(m[1], 10) : 1;
  }

  function tierForMonths(months) {
    if (months >= 4) return 3;
    if (months >= 2) return 2;
    if (months >= 1) return 1;
    return 0;
  }

  function cartTier(cart, cfg) {
    var best = 0;
    cart.items.forEach(function (item) {
      var t = tierForMonths(monthsForLine(item, cfg));
      if (t > best) best = t;
    });
    return best;
  }

  function isGiftLine(item, cfg) {
    if (!item.properties || !item.properties._ms_gift) return false;
    return !!cfg.giftByVariant[item.variant_id];
  }

  /* ------------------------------------------------------------------ state */

  function readState(token) {
    /* Shopify's cart token carries a ?key= suffix that ROTATES across mutations while
       the cart itself lives on. Keying state to the full string resets it midway
       through a session, forgetting opt outs and re adding gifts a shopper deleted.
       The base token before the ? is the cart's stable identity. */
    token = String(token || '').split('?')[0];
    var s = null;
    try { s = JSON.parse(localStorage.getItem(STORE_KEY)); } catch (e) { /* degrade */ }
    if (!s || s.token !== token) s = { token: token, added: [], optout: [], blocked: [], priceStrikes: {}, blockedTs: {} };
    if (!s.priceStrikes) s.priceStrikes = {};
    if (!s.blockedTs) s.blockedTs = {};
    return s;
  }
  /* A block protects against add/remove loops, but it must never strip an honest
     customer's gifts forever over a transient glitch: blocks expire after 30 minutes. */
  function isBlocked(state, variant) {
    if (state.blocked.indexOf(variant) === -1) return false;
    var ts = state.blockedTs[String(variant)] || 0;
    if (Date.now() - ts > 1800000) {
      state.blocked = without(state.blocked, variant);
      delete state.blockedTs[String(variant)];
      return false;
    }
    return true;
  }
  function block(state, variant) {
    if (state.blocked.indexOf(variant) === -1) state.blocked.push(variant);
    state.blockedTs[String(variant)] = Date.now();
  }
  function writeState(state) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }
  function without(list, v) { return list.filter(function (x) { return x !== v; }); }

  /* -------------------------------------------------------------- transport */

  function cartFetch(url, body, attempt) {
    var init = { headers: { 'Accept': 'application/json', 'X-MS-Gift': '1' } };
    if (body) {
      init.method = 'POST';
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return fetch(url, init).then(function (r) {
      /* Storefront throttling (429) or a hiccup must never leave the cart half
         reconciled: one in-place retry after a polite pause. */
      if ((r.status === 429 || r.status >= 500) && !attempt) {
        return new Promise(function (res) { setTimeout(res, 4500); })
          .then(function () { return cartFetch(url, body, 1); });
      }
      return r;
    });
  }
  function fetchCart() {
    return cartFetch('/cart.js').then(function (r) {
      return r.text().then(function (t) {
        if (t && t.charAt(0) === '{') return JSON.parse(t);
        throw new Error('cart.js unavailable (' + r.status + ')');
      });
    });
  }
  function addGift(gift, tier) {
    var props = {};
    props._ms_gift = gift.key;
    return cartFetch('/cart/add.js', { items: [{ id: gift.variant, quantity: 1, properties: props }] });
  }
  function changeLine(key, quantity) {
    return cartFetch('/cart/change.js', { id: key, quantity: quantity });
  }

  /* ------------------------------------------------------------- reconciler */

  function reconcile(forceRepaint) {
    var cfg = config();
    if (!cfg) return;
    if (BUSY) { QUEUED = true; return; }
    BUSY = true;

    fetchCart().then(function (cart) {
      var state = readState(cart.token);
      var tier = cartTier(cart, cfg);
      var giftLines = cart.items.filter(function (i) { return isGiftLine(i, cfg); });
      var chain = Promise.resolve();
      var mutated = !!forceRepaint;

      /* 1. Remove what must not be there, clamp what grew.
            Every mutation re reads the cart first: keys shift on every change. */
      giftLines.forEach(function (line) {
        var gift = cfg.giftByVariant[line.variant_id];
        var overTier = gift.tier > tier;                       /* includes tier 0 */
        var priced = line.final_line_price > 0 && line.quantity === 1;
        var strikes = state.priceStrikes[String(line.variant_id)] || 0;

        /* THE INVARIANT. One grace pass covers the discount landing a beat late
           right after an add; the second consecutive sighting removes and blocks. */
        var priceViolation = false;
        if (!overTier && priced) {
          if (strikes >= 1) {
            priceViolation = true;
          } else {
            state.priceStrikes[String(line.variant_id)] = strikes + 1;
            mutated = true; /* force a follow up verify pass */
          }
        } else {
          delete state.priceStrikes[String(line.variant_id)];
        }

        var shouldRemove = overTier || priceViolation;
        var shouldClamp = !shouldRemove && line.quantity > 1;
        if (!shouldRemove && !shouldClamp) return;
        mutated = true;
        chain = chain.then(function () {
          return fetchCart().then(function (freshCart) {
            var fresh = null;
            freshCart.items.forEach(function (i) {
              if (!fresh && isGiftLine(i, cfg) && i.variant_id === line.variant_id) fresh = i;
            });
            if (!fresh) return;
            return changeLine(fresh.key, shouldRemove ? 0 : 1);
          }).then(function () {
            if (shouldRemove) {
              state.added = without(state.added, line.variant_id);
              delete state.priceStrikes[String(line.variant_id)];
            }
            if (priceViolation) block(state, line.variant_id);
          });
        });
      });

      /* 2. Respect a shopper's manual removal for the life of this cart. */
      cfg.gifts.forEach(function (gift) {
        var present = giftLines.some(function (l) { return l.variant_id === gift.variant; });
        if (!present && gift.tier <= tier && state.added.indexOf(gift.variant) !== -1 && state.optout.indexOf(gift.variant) === -1) {
          state.optout.push(gift.variant);
          state.added = without(state.added, gift.variant);
        }
      });

      /* 3. Add qualifying missing gifts, one request each so one sold out gift
            cannot poison the rest. */
      cfg.gifts.forEach(function (gift) {
        var present = giftLines.some(function (l) { return l.variant_id === gift.variant; });
        var skipped = state.optout.indexOf(gift.variant) !== -1 || isBlocked(state, gift.variant);
        if (present || skipped || gift.tier > tier || tier === 0) return;
        mutated = true;
        chain = chain.then(function () {
          return addGift(gift, tier).then(function (r) {
            if (r.ok) {
              if (state.added.indexOf(gift.variant) === -1) state.added.push(gift.variant);
            } else {
              block(state, gift.variant); /* sold out or unavailable; expires in 30 min */
            }
          });
        });
      });

      return chain.then(function () {
        writeState(state);
        return fetchCart().then(function (finalCart) {
          paintUpgrade(finalCart, cfg);
          if (!mutated) return;
          updateCartCount(finalCart);
          return repaint(finalCart);
        });
      });
    }).then(function () {
      FAILS = 0;
    }).catch(function (err) {
      if (window.console && console.warn) console.warn('ms-gift-ladder:', err);
      /* Self heal: a throttled or failed pass retries with backoff (bounded, event
         driven, resets on success) so the cart never stays half reconciled. */
      FAILS += 1;
      if (FAILS <= 3) setTimeout(function () { reconcile(); }, 5000 * FAILS);
    }).then(function () {
      BUSY = false;
      if (QUEUED) { QUEUED = false; reconcile(); }
    });
  }
  var FAILS = 0;

  function scheduleReconcile() {
    clearTimeout(DEBOUNCE);
    DEBOUNCE = setTimeout(function () { reconcile(); }, 250);
  }

  /* ----------------------------------------------------------- DOM repaint */

  function updateCartCount(cart) {
    document.querySelectorAll('cart-count').forEach(function (el) {
      try {
        if ('itemCount' in el) { el.itemCount = cart.item_count; } else { el.textContent = cart.item_count; }
      } catch (e) { el.textContent = cart.item_count; }
    });
  }

  /* Repaint: the drawer knows how to redraw itself from the server (theme's own
     cart:refresh listener) — use that, it is the native path. The /cart page falls
     back to a section swap. */
  function repaint(cart) {
    if (document.querySelector('cart-drawer')) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      var cfg = config();
      if (cfg) setTimeout(function () { fetchCart().then(function (c) { paintUpgrade(c, cfg); }).catch(function () {}); }, 900);
      return;
    }
    var sectionId = null;
    if (document.getElementById('shopify-section-main-cart')) sectionId = 'main-cart';
    else {
      var mc = document.querySelector('[id^="shopify-section"][id*="main-cart"]');
      if (mc) sectionId = mc.id.replace('shopify-section-', '');
    }
    if (!sectionId) return;

    return fetch('/?sections=' + encodeURIComponent(sectionId), { headers: { 'X-MS-Gift': '1' } })
      .then(function (r) { return r.json(); })
      .then(function (payload) {
        var html = payload[sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var host = document.getElementById('shopify-section-' + sectionId);
        if (!host) return;
        var swapped = false;
        ['.cart-drawer__line-items', '.cart-drawer__footer', '.cart-form__totals', '.cart-drawer__inner'].forEach(function (sel) {
          if (swapped && sel === '.cart-drawer__inner') return; /* only fall back to inner when nothing else matched */
          var from = doc.querySelector(sel);
          var to = host.querySelector(sel);
          if (from && to) { to.innerHTML = from.innerHTML; swapped = true; }
        });
        if (!swapped) host.innerHTML = doc.body.firstElementChild ? doc.body.firstElementChild.innerHTML : html;
        var cfg = config();
        if (cfg) paintUpgrade(cart, cfg);
      })
      .catch(function () { /* repaint is cosmetic; the cart itself is already correct */ });
  }

  /* ------------------------------------------------------- upgrade buttons */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Shopify CDN size suffix. Cart line images come through full size; the picker
     tiles are ~100px, so an unsized hero is a wasted megabyte on mobile data. */
  function thumb(url, px) {
    if (!url) return url;
    return String(url).replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_' + px + 'x$1$2');
  }

  /* Shopify's OWN first-order price for this variant on this plan, emitted into the
     config by the snippet. Never computed here: multiplying by a discount percent
     drifts from what Skio actually bills the moment a plan is edited. Null means we
     could not prove a price, and the picker then refuses to render at all. */
  function subPrice(cfg, variantId, planId) {
    var key = String(variantId) + ':' + String(planId), found = null;
    Object.keys(cfg.products).forEach(function (pid) {
      if (found !== null) return;
      var pp = cfg.products[pid].planPrices;
      if (pp && typeof pp[key] === 'number') found = pp[key];
    });
    return found;
  }

  /* Renewal price, only when the plan actually declares a separate renewal rate.
     No rate -> null -> the drawer says nothing about renewals rather than guessing. */
  function renewPrice(cfg, onceCents, planId) {
    var pct = cfg.renewPct ? cfg.renewPct[String(planId)] : null;
    if (typeof pct !== 'number') return null;
    return Math.round(onceCents * (100 - pct) / 100);
  }
  function renewPct(cfg, planId) {
    var pct = cfg.renewPct ? cfg.renewPct[String(planId)] : null;
    return typeof pct === 'number' ? pct : null;
  }

  function upgradeOffers(cart, cfg) {
    /* Highest months subscription line in scope drives the offer. */
    var target = null;
    cart.items.forEach(function (item) {
      var months = monthsForLine(item, cfg);
      if (months > 0 && (!target || months > target.months)) {
        target = { line: item, months: months };
      }
    });

    /* No subscription in the cart: offer the SUBSCRIBE upsell on an in-scope one time
       line (IM8's drawer move). Converting lands them on the monthly plan; the higher
       supply upsells then take over on the next paint. */
    if (!target) {
      var oneTime = null;
      cart.items.forEach(function (item) {
        if (oneTime || item.selling_plan_allocation) return;
        if (item.merchandise === undefined && !cfg.products[String(item.product_id)]) return;
        if (!cfg.products[String(item.product_id)]) return;
        if (cfg.giftByVariant[item.variant_id]) return; /* never upsell a gift line */
        oneTime = item;
      });
      if (!oneTime) return [];
      var t1cents = 0, imgs1 = [], gifts1 = [];
      cfg.gifts.forEach(function (g) {
        if (g.tier !== 1) return;
        t1cents += g.cents;
        gifts1.push(g);
        if (g.img) imgs1.push({ src: g.img, alt: g.title });
      });
      var planId1 = cfg.planByMonths['1'];
      if (!planId1) return [];

      var qty1 = oneTime.quantity || 1;
      var oncePer = oneTime.price;
      var subPer = subPrice(cfg, oneTime.variant_id, planId1);
      var renewPer = subPer === null ? null : renewPrice(cfg, oncePer, planId1);
      /* Gifts are per cart; the plan discount is per unit. Add them the way the
         shopper experiences them or a qty 2 bag overstates the free goods. */
      var cutPer = subPer === null ? 0 : Math.max(0, oncePer - subPer);

      return [{
        months: 1, fromOneTime: true,
        /* picker payload: only set when every price is provable */
        picker: subPer === null ? null : {
          gifts: gifts1,
          giftCents: t1cents,
          onceCents: oncePer * qty1,
          subCents: subPer * qty1,
          renewCents: renewPer === null ? null : renewPer * qty1,
          cutCents: cutPer * qty1,
          saveCents: t1cents + (cutPer * qty1),
          renewPct: renewPct(cfg, planId1),
          seedImg: oneTime.image,
          seedTitle: oneTime.product_title || oneTime.title
        },
        /* the original one line cell, kept as the fallback when a price is missing */
        bold: 'Get 2 free gifts (' + money(t1cents) + ' value)',
        sub: 'switch to a monthly subscription',
        imgs: imgs1,
        lineKey: oneTime.key, quantity: qty1,
        oldVariant: oneTime.variant_id,
        variant: oneTime.variant_id, plan: planId1
      }];
    }

    if (target.months >= 4) return [];
    var product = cfg.products[String(target.line.product_id)];
    if (!product) return [];
    var offers = [];
    [2, 4].forEach(function (m) {
      if (m <= target.months) return;
      var variantId = product.byMonths[String(m)];
      var planId = cfg.planByMonths[String(m)];
      if (!variantId || !planId) return;
      var bold, sub, giftKeys;
      if (m === 2) {
        bold = 'Add the whisk, free';
        sub = 'switch to the 2 month supply';
        giftKeys = ['whisk'];
      } else {
        bold = target.months >= 2 ? 'Add the hojicha, free' : 'Add the whisk and hojicha, free';
        sub = 'switch to the 4 month supply';
        giftKeys = target.months >= 2 ? ['hojicha'] : ['whisk', 'hojicha'];
      }
      var imgs = [];
      giftKeys.forEach(function (k) {
        cfg.gifts.forEach(function (g) { if (g.key === k && g.img) imgs.push({ src: g.img, alt: g.title }); });
      });
      offers.push({
        months: m, bold: bold, sub: sub, imgs: imgs,
        lineKey: target.line.key, quantity: target.line.quantity,
        oldVariant: target.line.variant_id,
        variant: variantId, plan: planId
      });
    });
    return offers;
  }

  var TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"' +
             ' stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>';

  function tileHTML(src, alt, caption, isGift) {
    return '<span class="mssub__tile' + (isGift ? ' mssub__tile--gift' : '') + '">' +
      (isGift ? '<span class="mssub__seal">free</span>' : '') +
      '<img class="mssub__pic" src="' + esc(thumb(src, 240)) + '" alt="' + esc(alt) +
      '" loading="lazy" width="100" height="100">' +
      '<span class="mssub__cap">' + esc(caption) + '</span>' +
    '</span>';
  }

  /* The owner-chosen picker ("version 2", 2026-10-12): one time and every month as
     two selectable cards, the free gift lineup living inside the subscription card.
     The circle shows the choice; the card and its button commit it, because
     committing rebuilds the cart line and costs money. */
  function pickerHTML(p) {
    var tiles = '';
    if (p.seedImg) tiles += tileHTML(p.seedImg, p.seedTitle, 'Your matcha', false);
    p.gifts.forEach(function (g) {
      if (!g.img) return;
      if (tiles) tiles += '<span class="mssub__plus" aria-hidden="true">+</span>';
      tiles += tileHTML(g.img, g.title, g.title, true);
    });

    var names = p.gifts.map(function (g) { return g.title; });
    var nameList = names.length > 1
      ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
      : (names[0] || 'your free gift');

    var perks = '';
    perks += '<li>' + TICK + '<span>' + esc(nameList) +
             ', <b>free</b>, worth ' + money(p.giftCents) + '</span></li>';
    if (p.cutCents > 0) {
      perks += '<li>' + TICK + '<span><b>' + money(p.cutCents) + ' off</b> today' +
               (p.renewPct !== null ? ', then ' + p.renewPct + '% off every delivery' : '') +
               '</span></li>';
    }
    perks += '<li>' + TICK + '<span>Skip, pause or cancel anytime</span></li>';

    var thenLine = p.renewCents !== null ? ', then ' + money(p.renewCents) + ' monthly' : '';

    /* Owner-chosen angle, 2026-10-12 ("option 4"): reframe the price as a daily cost.
       Billed off the RENEWAL price, not the discounted first box, because the per-day
       figure describes what they go on paying; quoting it off the cheaper first order
       would understate the real cost. Falls back to the first-order price only when the
       plan declares no separate renewal rate, and to the plain "Best value" bar when no
       price is provable at all. 30 is the month we divide by, so the number is stable
       instead of sliding between a 28 and a 31 day month. */
    var dayBase = p.renewCents !== null ? p.renewCents : p.subCents;
    var hasDay = typeof dayBase === 'number' && dayBase > 0;
    var ribbonIcon = hasDay
      ? '<path d="M3 7h18v14H3zM8 3v4M16 3v4M3 11h18"/>'
      : '<path d="M12 2l2.9 6.2 6.6.9-4.8 4.7 1.2 6.7L12 17.3 6.1 20.5l1.2-6.7L2.5 9.1l6.6-.9z"/>';
    var ribbonText = hasDay ? 'About ' + money(dayBase / 30) + ' a day' : 'Best value';

    return '' +
      '<div class="mssub">' +
        '<div class="mssub__choice mssub__choice--on">' +
          '<span class="mssub__radio" aria-hidden="true"></span>' +
          '<span class="mssub__main">' +
            '<span class="mssub__name">Just this once</span>' +
            '<span class="mssub__note">In your bag now</span>' +
          '</span>' +
          '<span class="mssub__right"><span class="mssub__price">' + money(p.onceCents) + '</span></span>' +
        '</div>' +
        '<div class="mssub__choice mssub__choice--offer" data-mssub-offer>' +
          /* NOT a popularity claim. Measured 2026-10-12: only ~3% of ceremonial units
             sold in the previous 90 days went out on a subscription, so "most popular"
             would be flatly untrue here. Both bars below are arithmetic on prices the
             shopper can see. Re-measure before ever putting social proof in this slot. */
          '<span class="mssub__ribbon">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"' +
            ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            ribbonIcon + '</svg>' +
            ribbonText +
          '</span>' +
          '<span class="mssub__radio" aria-hidden="true"></span>' +
          '<span class="mssub__main">' +
            '<span class="mssub__name">Every month</span>' +
            '<span class="mssub__note">Delivered monthly, free shipping</span>' +
          '</span>' +
          '<span class="mssub__right">' +
            '<span class="mssub__price">' + money(p.subCents) + '</span>' +
            (p.cutCents > 0 ? '<s class="mssub__was">' + money(p.onceCents) + '</s>' : '') +
            (p.saveCents > 0 ? '<span class="mssub__save">Save ' + money(p.saveCents) + '</span>' : '') +
          '</span>' +
          '<span class="mssub__open">' +
            '<span class="mssub__h">Your first box arrives with these</span>' +
            '<span class="mssub__row">' + tiles + '</span>' +
            '<ul class="mssub__perks">' + perks + '</ul>' +
            '<button class="mssub__cta" type="button" data-mssub-go>' +
              '<strong>Switch to every month</strong>' +
              '<span>' + money(p.subCents) + ' today' + thenLine + '</span>' +
            '</button>' +
            /* Supporting line for the daily-cost angle. The cafe comparison is a
               deliberately small claim and stays BELOW the button, never the lead:
               the brand rule is that "cheaper than the coffee shop" supports, it
               never headlines. Dropped entirely when there is no per-day figure. */
            (hasDay ? '<span class="mssub__flagline">Less than one cafe matcha a day</span>' : '') +
          '</span>' +
        '</div>' +
      '</div>' +
      '<p class="mssub__fine">Gifts ship with your first box.</p>';
  }

  function paintPicker(wrap, offer) {
    wrap.innerHTML = pickerHTML(offer.picker);
    var card = wrap.querySelector('[data-mssub-offer]');
    var btn = wrap.querySelector('[data-mssub-go]');
    if (!card || !btn) return;
    /* ONE listener, on the card. The button sits inside it, so its clicks bubble
       here and fire exactly once; binding both would run the upgrade twice. */
    card.addEventListener('click', function () { runUpgrade(offer, btn, card); });
  }

  function paintUpgrade(cart, cfg) {
    var shells = document.querySelectorAll('[data-msgift-upgrade]');
    if (!shells.length) return;
    var offers = upgradeOffers(cart, cfg);
    shells.forEach(function (shell) {
      var wrap = shell.querySelector('[data-msgift-upgrade-buttons]');
      if (!wrap) return;
      var titleEl = shell.querySelector('[data-msgift-upgrade-title]');
      if (!offers.length) { shell.hidden = true; wrap.innerHTML = ''; return; }
      wrap.innerHTML = '';

      /* The subscribe conversion renders as the picker; supply upgrades keep the
         original stacked buttons. A missing price falls back to those buttons too. */
      if (offers.length === 1 && offers[0].picker) {
        if (titleEl) {
          titleEl.textContent = 'How would you like it';
          titleEl.classList.add('is-picker');
        }
        paintPicker(wrap, offers[0]);
        shell.hidden = false;
        return;
      }
      if (titleEl) {
        titleEl.textContent = 'Unlock more free gifts';
        titleEl.classList.remove('is-picker');
      }

      offers.forEach(function (offer) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'msgift-upgrade__btn';
        btn.setAttribute('data-months', offer.months);
        var thumbs = '';
        (offer.imgs || []).forEach(function (im) {
          thumbs += '<img class="msgift-upgrade__thumb" src="' + im.src + '" alt="' + im.alt.replace(/"/g, '&quot;') + '" loading="lazy" width="44" height="44">';
        });
        btn.innerHTML =
          (thumbs ? '<span class="msgift-upgrade__thumbs">' + thumbs + '</span>' : '') +
          '<span class="msgift-upgrade__text"><strong>' + offer.bold + '</strong><span>' + offer.sub + '</span></span>';
        btn.addEventListener('click', function () { runUpgrade(offer, btn); });
        wrap.appendChild(btn);
      });
      shell.hidden = false;
    });
  }

  function runUpgrade(offer, btn, card) {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-busy');
    if (card) card.classList.add('mssub__choice--busy');
    /* ADD FIRST, REMOVE SECOND. If the add fails, the customer's cart is untouched.
       If the remove fails, they briefly see both supply lines (recoverable by hand or
       by the next pass) — the one failure mode we never allow is losing their
       subscription line. The old line is re-located by variant at click time so a
       stale key from an earlier paint can never remove the wrong line. */
    cartFetch('/cart/add.js', {
      items: [{ id: offer.variant, quantity: offer.quantity || 1, selling_plan: offer.plan }]
    })
      .then(function (r) {
        if (!r.ok) throw new Error('upgrade add failed');
        return fetchCart();
      })
      .then(function (cart) {
        /* Locate the line being replaced by variant at THIS moment: a subscription
           line for supply upgrades, a plan-less line for the subscribe conversion.
           For the conversion the add MERGED nothing (same variant, new plan = new
           line), so the old plan-less line is still there to remove. */
        var old = null;
        cart.items.forEach(function (i) {
          if (old || i.variant_id !== offer.oldVariant) return;
          if (offer.fromOneTime ? !i.selling_plan_allocation : i.selling_plan_allocation) old = i;
        });
        return old ? changeLine(old.key, 0) : null;
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        updateCartCount(cart);
        return repaint(cart);
      })
      .then(function () { reconcile(true); })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('ms-gift-ladder upgrade:', err);
        reconcile(true);
      })
      .then(function () {
        btn.disabled = false;
        btn.classList.remove('is-busy');
        if (card) card.classList.remove('mssub__choice--busy');
      });
  }

  /* The PDP gift rows are pure Liquid + CSS: they live inside each subscription card
     and unfold only on the selected card (the .msup__perks pattern). No JS needed. */

  /* ------------------------------------------------------------- triggers */

  /* Any cart mutation from ANY code (theme, Skio, apps) schedules a reconcile.
     Our own requests carry X-MS-Gift and are ignored to avoid loops. */
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var ours = init && init.headers && (init.headers['X-MS-Gift'] || (typeof init.headers.get === 'function' && init.headers.get('X-MS-Gift')));
    var p = origFetch.apply(this, arguments);
    if (!ours && /\/cart\/(add|change|update|clear)/.test(url)) {
      p.then(function () { scheduleReconcile(); }, function () {});
    }
    return p;
  };

  document.addEventListener('cart:change', scheduleReconcile);
  document.addEventListener('variant:add', scheduleReconcile);
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleReconcile(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleReconcile();
  });

  /* Key based removal (ported from the July engine) with INSTANT gift cleanup: when
     the removed line is the subscription that earned the gifts, the SAME single
     request also drops every gift the remaining cart no longer earns, and the rows
     hide from the screen immediately (optimistic; the repaint confirms). */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[data-ms-line-key]');
    if (!link) return;
    event.preventDefault();
    /* The theme's own <line-item-quantity> also listens for clicks on this anchor and
       fires its own removal request. Two mutations racing means the loser posts a line
       key the winner already deleted, Shopify 404s it, and the shopper lands on a hard
       error page. This handler must be the gesture's ONLY mutator. */
    event.stopPropagation();
    if (link.__msRemoving) return;
    link.__msRemoving = true;
    var key = link.getAttribute('data-ms-line-key');
    var cfg = config();

    fetchCart()
      .then(function (cart) {
        var updates = {};
        updates[key] = 0;
        var goneVariants = [];
        /* A shopper deleting a GIFT line is an opt out: record it before the request,
           so no later reconcile pass can hand the gift back. (Gift lines swept out
           below only because their tier lapsed are NOT opt outs: those return if a
           qualifying subscription is added again.) */
        if (cfg) {
          var clicked = null;
          cart.items.forEach(function (i) { if (i.key === key) clicked = i; });
          if (clicked && isGiftLine(clicked, cfg)) {
            var st = readState(cart.token);
            if (st.optout.indexOf(clicked.variant_id) === -1) st.optout.push(clicked.variant_id);
            st.added = without(st.added, clicked.variant_id);
            writeState(st);
          }
        }
        if (cfg) {
          /* What tier does the cart earn WITHOUT the removed line? */
          var remaining = { items: cart.items.filter(function (i) { return i.key !== key; }) };
          var tierAfter = cartTier(remaining, cfg);
          remaining.items.forEach(function (i) {
            if (isGiftLine(i, cfg) && cfg.giftByVariant[i.variant_id].tier > tierAfter) {
              updates[i.key] = 0;
              goneVariants.push(i.variant_id);
            }
          });
        }
        /* Optimistic: hide the clicked row and every gift row leaving with it. */
        var row = link.closest('line-item');
        if (row) row.style.display = 'none';
        goneVariants.forEach(function (v) {
          document.querySelectorAll('line-item[data-ms-variant="' + v + '"]').forEach(function (el) {
            el.style.display = 'none';
          });
        });
        return cartFetch('/cart/update.js', { updates: updates });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('remove failed');
        return fetchCart();
      })
      .then(function (cart) {
        updateCartCount(cart);
        return repaint(cart);
      })
      .then(function () { reconcile(true); link.__msRemoving = false; })
      .catch(function () {
        /* Never fall back to the link's raw /cart/change URL: its line key is stale in
           exactly the states that reach this catch, and Shopify renders a hard error
           page ("no valid id or line parameter") for a stale key. The cart page always
           renders the cart's true current state. */
        window.location.href = '/cart';
      });
  }, true);

  function boot() {
    reconcile();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
